import React, { useState, useEffect } from 'react';
import { Calendar, ArrowRight, Clock, Tag, ExternalLink, Newspaper, AlertCircle } from 'lucide-react';
import { useLang } from '@/i18n/LangContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogSection() {
  const { t } = useLang();
  const [posts, setPosts] = useState([]);
  const [pressArticles, setPressArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [viewMode, setViewMode] = useState('blog');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/blog`).then(r => r.data).catch(() => []),
      axios.get(`${API}/press`).then(r => r.data).catch(() => []),
    ]).then(([blogData, pressData]) => {
      setPosts(blogData);
      setPressArticles(pressData);
    }).finally(() => setLoading(false));
  }, []);

  const categories = ['all', ...new Set(posts.map(p => p.category))];
  const filtered = activeCategory === 'all' ? posts.slice(0, 6) : posts.filter(p => p.category === activeCategory).slice(0, 6);

  // Group press articles by product
  const groupedPress = pressArticles.reduce((acc, article) => {
    const excerpt = article.excerpt || '';
    let product = 'Other';
    if (excerpt.includes('PROPESIA360')) product = 'PROPESIA360';
    else if (excerpt.includes('JAPAP MESSENGER')) product = 'JAPAP MESSENGER';
    else if (excerpt.includes('EAA INVEST')) product = 'EAA INVEST';
    if (!acc[product]) acc[product] = [];
    acc[product].push(article);
    return acc;
  }, {});

  const productOrder = ['PROPESIA360', 'JAPAP MESSENGER', 'EAA INVEST', 'Other'];
  const productColors = { 'PROPESIA360': '#00D186', 'JAPAP MESSENGER': '#0A75E5', 'EAA INVEST': '#00D186' };

  return (
    <section id="blog" data-testid="blog-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00D186] mb-4 block">{t.blog.label}</span>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight text-[#0B132B] mb-4">{t.blog.title1} <span className="text-gradient">{t.blog.title2}</span></h2>
          <p className="text-base md:text-lg text-[#475569] max-w-2xl mx-auto">{t.blog.desc}</p>
        </div>

        <div className="flex justify-center gap-2 mb-8">
          <button onClick={() => setViewMode('blog')} data-testid="view-blog" className={`px-6 py-2.5 rounded-sm text-sm font-bold tracking-wide transition-all duration-300 ${viewMode === 'blog' ? 'bg-[#0B132B] text-white' : 'bg-[#F3F5F7] text-[#475569] hover:bg-[#E2E8F0]'}`}>
            Blog ({posts.length})
          </button>
          <button onClick={() => setViewMode('press')} data-testid="view-press" className={`px-6 py-2.5 rounded-sm text-sm font-bold tracking-wide transition-all duration-300 flex items-center gap-1.5 ${viewMode === 'press' ? 'bg-[#0B132B] text-white' : 'bg-[#F3F5F7] text-[#475569] hover:bg-[#E2E8F0]'}`}>
            <Newspaper size={14} /> Press ({pressArticles.length})
          </button>
        </div>

        {viewMode === 'blog' && (
          <>
            <div className="flex flex-wrap justify-center gap-2 mb-12" data-testid="blog-categories">
              {categories.map((cat) => (
                <button key={cat} onClick={() => setActiveCategory(cat)} data-testid={`blog-category-${cat}`} className={`px-5 py-2 rounded-sm text-sm font-bold tracking-wide transition-all duration-300 ${activeCategory === cat ? 'bg-[#00D186] text-[#0B132B]' : 'bg-[#F3F5F7] text-[#475569] hover:bg-[#E2E8F0]'}`}>
                  {cat === 'all' ? t.blog.all : cat}
                </button>
              ))}
            </div>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">{[1,2,3].map(i => <div key={i} className="bg-[#F3F5F7] rounded-xl h-[400px] animate-pulse" />)}</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filtered.map((post, i) => (
                  <article key={post.id} data-testid={`blog-post-${i}`} className="group bg-white border border-[#E2E8F0] rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                    <div className="relative h-52 overflow-hidden">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute top-4 left-4"><span className="inline-flex items-center gap-1 px-3 py-1 bg-[#00D186] text-[#0B132B] text-xs font-bold rounded-sm"><Tag size={12} />{post.category}</span></div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-xs text-[#94A3B8] mb-3">
                        <span className="flex items-center gap-1"><Calendar size={12} />{new Date(post.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                        <span className="flex items-center gap-1"><Clock size={12} />{post.read_time}</span>
                      </div>
                      <h3 className="text-lg font-bold text-[#0B132B] mb-2 leading-tight group-hover:text-[#0A75E5] transition-colors">{post.title}</h3>
                      <p className="text-sm text-[#475569] leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
                      <button data-testid={`blog-read-more-${i}`} className="inline-flex items-center gap-1 text-sm font-bold text-[#0B132B] hover:text-[#00D186] transition-colors">{t.blog.readMore}<ArrowRight size={14} /></button>
                    </div>
                  </article>
                ))}
              </div>
            )}
            {filtered.length === 0 && !loading && <p className="text-center text-[#94A3B8] py-12">{t.blog.noArticles}</p>}
          </>
        )}

        {/* Press Articles grouped by product */}
        {viewMode === 'press' && (
          <div>
            {/* Disclaimer */}
            <div className="bg-[#F3F5F7] border border-[#E2E8F0] rounded-xl p-5 mb-10 flex items-start gap-3" data-testid="press-disclaimer">
              <AlertCircle size={18} className="text-[#94A3B8] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#475569] italic">{t.services.s7note}</p>
            </div>

            {productOrder.filter(p => groupedPress[p]).map((product) => (
              <div key={product} className="mb-12" data-testid={`press-group-${product.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-8 rounded-full" style={{ backgroundColor: productColors[product] || '#94A3B8' }} />
                  <h3 className="text-xl font-bold text-[#0B132B]">{product}</h3>
                  <span className="text-xs text-[#94A3B8] bg-[#F3F5F7] px-2 py-1 rounded">{groupedPress[product].length} publications</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedPress[product].map((article, i) => (
                    <a
                      key={article.id}
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`press-article-${product.toLowerCase().replace(/\s/g, '-')}-${i}`}
                      className="group bg-white border border-[#E2E8F0] rounded-xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-[#0A75E5]/30 block"
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#0A75E5]/10 text-[#0A75E5] text-xs font-bold rounded-sm flex-shrink-0">
                          {article.source}
                        </span>
                        <ExternalLink size={14} className="text-[#94A3B8] group-hover:text-[#0A75E5] transition-colors flex-shrink-0 mt-1" />
                      </div>
                      <h4 className="text-sm font-bold text-[#0B132B] mb-2 leading-tight group-hover:text-[#0A75E5] transition-colors line-clamp-2">
                        {article.title}
                      </h4>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-[#94A3B8] flex items-center gap-1">
                          <Calendar size={10} />
                          {new Date(article.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </span>
                        <span className="text-xs text-[#94A3B8] italic">Press release</span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
